
import { useState } from 'react';
import Sidebar from '../components/Sidebar';

interface Appointment {
  id: string;
  date: string;
  time: string;
  customerName: string;
  type: string;
  artist: string;
  location: string;
  status: string;
  memo: string;
}

export default function MakeupSchedule() {
  const [currentDate, setCurrentDate] = useState(new Date(2024, 2, 1)); // 2024년 3월
  const [selectedDate, setSelectedDate] = useState('2024-03-15');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const [appointments, setAppointments] = useState<Appointment[]>([
    {
      id: '1',
      date: '2024-03-15',
      time: '09:00',
      customerName: '김미영',
      type: '본식메이크업',
      artist: '박실장',
      location: '매장',
      status: '확정',
      memo: '내추럴 톤, 피부 민감'
    },
    {
      id: '2',
      date: '2024-03-15',
      time: '11:00',
      customerName: '이지은',
      type: '리허설메이크업',
      artist: '김원장',
      location: '매장',
      status: '확정',
      memo: '글램 스타일 선호'
    },
    {
      id: '3',
      date: '2024-03-15',
      time: '14:00',
      customerName: '박수진',
      type: '혼주메이크업',
      artist: '최팀장',
      location: '출장',
      status: '확정',
      memo: '강남웨딩홀 출장'
    },
    {
      id: '4',
      date: '2024-03-16',
      time: '10:00',
      customerName: '정민호',
      type: '본식메이크업',
      artist: '박실장',
      location: '출장',
      status: '대기',
      memo: '웨딩홀 출장 예정'
    },
    {
      id: '5',
      date: '2024-03-20',
      time: '13:00',
      customerName: '한소영',
      type: '리허설메이크업',
      artist: '김원장',
      location: '매장',
      status: '확정',
      memo: '세미 글램 스타일'
    }
  ]);

  const appointmentTypes = ['본식메이크업', '리허설메이크업', '혼주메이크업', '출장메이크업'];
  const artists = ['김원장', '박실장', '최팀장', '이아티스트', '프리랜서A'];
  const locations = ['매장', '출장'];
  const statusTypes = ['확정', '대기', '취소'];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getAppointmentsForDate = (dateString: string) => {
    return appointments.filter(appointment => appointment.date === dateString);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    const weekHeader = weekDays.map(day => (
      <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50">
        {day}
      </div>
    ));

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-28 border border-gray-200"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDate(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayAppointments = getAppointmentsForDate(dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];
      const isSelected = dateString === selectedDate;

      days.push(
        <div
          key={day}
          className={`p-2 h-28 border border-gray-200 cursor-pointer hover:bg-gray-50 ${
            isToday ? 'bg-pink-50' : ''
          } ${isSelected ? 'bg-pink-100' : ''}`}
          onClick={() => setSelectedDate(dateString)}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-pink-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {dayAppointments.slice(0, 2).map((appointment) => (
              <div
                key={appointment.id}
                className={`text-xs p-1 rounded truncate cursor-pointer ${
                  appointment.type === '본식메이크업' ? 'bg-pink-100 text-pink-800' :
                  appointment.type === '리허설메이크업' ? 'bg-purple-100 text-purple-800' :
                  appointment.type === '혼주메이크업' ? 'bg-blue-100 text-blue-800' :
                  'bg-orange-100 text-orange-800'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditAppointment(appointment);
                }}
              >
                {appointment.time} {appointment.customerName}
              </div>
            ))}
            {dayAppointments.length > 2 && (
              <div className="text-xs text-gray-500">
                +{dayAppointments.length - 2}개 더
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7">
          {weekHeader}
          {days}
        </div>
      </div>
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const newAppointment: Appointment = {
      id: selectedAppointment ? selectedAppointment.id : (appointments.length + 1).toString(),
      date: formData.get('date') as string,
      time: formData.get('time') as string,
      customerName: formData.get('customerName') as string,
      type: formData.get('type') as string,
      artist: formData.get('artist') as string,
      location: formData.get('location') as string,
      status: formData.get('status') as string,
      memo: formData.get('memo') as string
    };

    if (selectedAppointment) {
      setAppointments(appointments.map(a => a.id === selectedAppointment.id ? newAppointment : a));
    } else {
      setAppointments([...appointments, newAppointment]);
    }

    setShowAddForm(false);
    setSelectedAppointment(null);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowAddForm(true);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">예약·스케줄 관리</h1>
            <p className="text-sm text-gray-600 mt-1">메이크업 예약 일정을 관리하세요</p>
          </div>
          <button
            onClick={() => {
              setSelectedAppointment(null);
              setShowAddForm(true);
            }}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            예약 등록
          </button>
        </div>

        {/* 캘린더 헤더 */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
            >
              <i className="ri-arrow-left-line text-gray-600"></i>
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
            </h2>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
            >
              <i className="ri-arrow-right-line text-gray-600"></i>
            </button>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-pink-100 rounded"></div>
              <span>본식</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-100 rounded"></div>
              <span>리허설</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-100 rounded"></div>
              <span>혼주</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-orange-100 rounded"></div>
              <span>출장</span>
            </div>
          </div>
        </div>

        {/* 캘린더 */}
        {renderCalendar()}

        {/* 선택된 날짜의 예약 목록 */}
        {selectedDate && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {selectedDate} 예약 일정
            </h3>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {getAppointmentsForDate(selectedDate).length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {getAppointmentsForDate(selectedDate).map((appointment) => (
                    <div key={appointment.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-medium text-gray-900">
                              {appointment.time}
                            </span>
                            <span className="font-medium text-gray-900">
                              {appointment.customerName}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              appointment.type === '본식메이크업' ? 'bg-pink-100 text-pink-800' :
                              appointment.type === '리허설메이크업' ? 'bg-purple-100 text-purple-800' :
                              appointment.type === '혼주메이크업' ? 'bg-blue-100 text-blue-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {appointment.type}
                            </span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              appointment.status === '확정' ? 'bg-green-100 text-green-800' :
                              appointment.status === '대기' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {appointment.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            아티스트: {appointment.artist} • {appointment.location}
                          </div>
                          {appointment.memo && (
                            <div className="text-sm text-gray-600 mt-1">
                              메모: {appointment.memo}
                            </div>
                          )}
                        </div>
                        <button 
                          onClick={() => handleEditAppointment(appointment)}
                          className="text-pink-600 hover:text-pink-800 w-6 h-6 flex items-center justify-center cursor-pointer"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  선택된 날짜에 예약이 없습니다.
                </div>
              )}
            </div>
          </div>
        )}

        {/* 예약 등록/수정 모달 */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {selectedAppointment ? '예약 수정' : '예약 등록'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setSelectedAppointment(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">날짜</label>
                    <input 
                      type="date" 
                      name="date"
                      defaultValue={selectedAppointment?.date || selectedDate}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">시간</label>
                    <input 
                      type="time" 
                      name="time"
                      defaultValue={selectedAppointment?.time || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                  <input 
                    type="text" 
                    name="customerName"
                    defaultValue={selectedAppointment?.customerName || ''}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메이크업 유형</label>
                    <select 
                      name="type"
                      defaultValue={selectedAppointment?.type || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                    >
                      <option value="">선택하세요</option>
                      {appointmentTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">아티스트</label>
                    <select 
                      name="artist"
                      defaultValue={selectedAppointment?.artist || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                    >
                      <option value="">선택하세요</option>
                      {artists.map(artist => (
                        <option key={artist} value={artist}>{artist}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">장소</label>
                    <select 
                      name="location"
                      defaultValue={selectedAppointment?.location || ''}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                    >
                      {locations.map(location => (
                        <option key={location} value={location}>{location}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                    <select 
                      name="status"
                      defaultValue={selectedAppointment?.status || '확정'}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                    >
                      {statusTypes.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea 
                    name="memo"
                    defaultValue={selectedAppointment?.memo || ''}
                    maxLength={500}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 h-20 resize-none"
                    placeholder="스타일 선호도, 피부 특이사항 등을 입력하세요"
                  ></textarea>
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setSelectedAppointment(null);
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 whitespace-nowrap cursor-pointer"
                  >
                    {selectedAppointment ? '수정' : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
